<template>
  <v-card elevation="0" rounded="0">
    <DrawerToolbar title="Add a resource" @close="close" />
    <div>
      <div class="pa-6">
        <Typography variant="h-6" class="mb-2 text-left">Available Resources</Typography>
        <v-text-field
          ref="search"
          outlined
          placeholder="Search"
          append-icon="mdi-magnify"
          single-line
          dense
          hide-details
          v-model="data.search"
          class="mb-2"
        ></v-text-field>
        <div class="d-flex justify-space-between align-center">
          <v-menu v-model="sort.menu" offset-y left :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-btn text color="brandPrimary" v-bind="attrs" v-on="on" class="text-uppercase px-0"
                >Sort</v-btn
              >
            </template>
            <v-list>
              <v-list-item
                v-for="option in sort.options"
                :key="option.id"
                @click="updateSortBy(option)"
                :class="{
                  'light-grey-background': option.values.includes(sort.value),
                }"
              >
                <v-list-item-content class="align-text-left">
                  <v-list-item-title>{{ option.label }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-icon :class="{ hidden: !option.values.includes(sort.value) }">
                  <Icon
                    :icon="
                      sort.value && sort.value.includes('asc') ? 'mdi-arrow-up' : 'mdi-arrow-down'
                    "
                  />
                </v-list-item-icon>
              </v-list-item>
            </v-list>
          </v-menu>
          <Button
            text
            class="text-uppercase"
            @click="loadContentForm"
            v-if="!loadingResources && !loading.resources"
            iconLeft="mdi-plus"
            small
          >
            Add Resource
          </Button>
        </div>
      </div>
      <div class="px-6 pb-6">
        <div class="d-flex justify-center" v-if="loadingResources || loading.resources">
          <v-progress-circular indeterminate color="primary" class="mx-auto"></v-progress-circular>
        </div>
        <div v-for="resource in visibleResources" :key="resource.id" class="mb-4">
          <div
            class="d-flex align-center cursor-pointer resource-row"
            @click="addToProgram(resource)"
          >
            <Icon
              :icon="resource.icon"
              color="white"
              class="resource-icons"
              :class="`${resource.iconColor}-background`"
            />
            <div class="ml-2 resource-box d-flex align-center pl-4 pr-2">
              <Typography variant="body-1" class="flex-grow-1 text-truncate text-left">{{
                resource.displayName
              }}</Typography>
              <Button
                :icon="true"
                small
                class="ml-2"
                @click.stop="downloadContent(resource)"
                :loading="!!loading.downloading[resource.contentId]"
              >
                <Icon icon="mdi-download" color="brand" small />
              </Button>
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                    <Icon icon="mdi-dots-vertical" color="brand" small />
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="openReplaceDialog(resource)">
                    <v-list-item-icon class="mr-2">
                      <Icon icon="mdi-upload" small />
                    </v-list-item-icon>
                    <v-list-item-title>Upload New Version</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openDeleteDialog(resource)">
                    <v-list-item-icon class="mr-2">
                      <Icon icon="mdi-delete" small />
                    </v-list-item-icon>
                    <v-list-item-title>Delete Document</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </div>
      </div>

      <ReplaceMediaDialog
        :show.sync="dialog.replace"
        :content="dialog.selectedResource"
        @replaced="onContentReplaced"
        @error="onContentError"
      />

      <ReplaceMediaDialog
        :show.sync="dialog.createContent"
        mode="create"
        :clientId="clientId"
        @created="onContentCreated"
        @error="onContentError"
      />

      <DeleteContentDialog
        :show.sync="dialog.delete"
        :content="dialog.selectedResource"
        @deleted="onContentDeleted"
        @in-use="onContentInUse"
        @error="onContentError"
      />

      <ErrorDialogV2
        v-model="dialog.deleteError"
        title="This resource is in use and can't be deleted"
        :message="inUseMessage"
        confirm-button-text="Okay"
      />
    </div>
  </v-card>
</template>

<script>
import ContentService from "@/services/ContentService";

import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Icon from "@/components/generic/Icon.vue";
import ReplaceMediaDialog from "@/components/content/ReplaceMediaDialog.vue";
import DeleteContentDialog from "@/components/content/DeleteContentDialog.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";

import { formatFileType } from "@/shared_data/functions.js";

import { mapState } from "vuex";
import moment from "moment";

export default {
  name: "ProgramResourceWidget",
  components: {
    DrawerToolbar,
    Typography,
    Button,
    Icon,
    ReplaceMediaDialog,
    DeleteContentDialog,
    ErrorDialogV2,
  },
  props: {
    clientId: Number,
    resources: Array,
    loadingResources: Boolean,
    activeProgram: Boolean,
    context: {
      type: String,
      default: "program",
    },
    programId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      data: {
        resources: [],
        search: null,
      },
      dialog: {
        createContent: false,
        replace: false,
        delete: false,
        deleteError: false,
        selectedResource: null,
        inUseReferences: [],
      },
      loading: {
        resources: false,
        downloading: {},
      },
      sort: {
        menu: false,
        value: "create-date-desc",
        options: [
          {
            label: "Name",
            key: "name",
            values: ["name-asc", "name-desc"],
          },
          {
            label: "Created",
            key: "create-date",
            values: ["create-date-asc", "create-date-desc"],
          },
        ],
      },
    };
  },

  created() {
    // We pass in props and fetch resources if not passed in
    if (!this.resources) this.getContent();
  },
  mounted() {},
  methods: {
    getContent(preloadContentId = null) {
      this.loading.resources = true;
      ContentService.getAllContent()
        .then((response) => {
          var resources = response
            .filter(
              (resource) =>
                resource.status !== "Deleted" &&
                (resource.clientId == 0 || resource.clientId == this.clientId) &&
                ![
                  "LEARNING_MODULE",
                  "LEARNING_CARD",
                  "LEARNING_CARD_CONTENT",
                  "SURVEY",
                  "badge",
                  "BADGE",
                ].includes(resource.category)
            )
            .map((x) => {
              var obj = x;
              obj.id = x.contentId.toString();
              obj.displayName = x.name;
              obj.context = "Content";
              obj.contextResourceId = x.contentId.toString();
              obj.contextColumn = "metricValue";
              obj.fileType = formatFileType(x.filePath);
              obj.label = x.name;
              obj.icon =
                obj.fileType == "PDF"
                  ? "mdi-file-pdf-box"
                  : obj.fileType == "DOCX"
                  ? "mdi-file-word"
                  : obj.fileType == "XLSX"
                  ? "mdi-file-excel"
                  : obj.fileType == "PPTX"
                  ? "mdi-file-powerpoint"
                  : obj.fileType == "PNG"
                  ? "mdi-file-image"
                  : obj.fileType == "CSV"
                  ? "mdi-file-delimited"
                  : "mdi-file";
              obj.iconColor = "brand";
              obj.displayGrabber = false;

              obj.createdAtFormatted = moment(obj.createdAt).utc().format("MMM DD, YYYY");
              return obj;
            });
          console.log("Content (within widget): ", resources);
          this.data.resources = resources;

          this.loading.resources = false;

          if (preloadContentId && typeof preloadContentId == "number") {
            const foundContent = resources.find((x) => x.contentId == preloadContentId);
            if (foundContent) this.addToProgram(foundContent);
          }
        })
        .catch((error) => {
          console.log("Error getting resources ", error);
          this.loading.resources = false;
        });
    },
    onContentCreated(response) {
      this.$store.dispatch("showSuccess", { message: "Resource created successfully" });
      if (this.context == "learning") {
        this.addToProgram(response);
        this.$emit("get-content");
      } else if (this.resources) {
        this.$emit("get-content", response.contentId);
      } else {
        this.getContent(response.contentId);
      }
    },
    async downloadContent(item) {
      this.$set(this.loading.downloading, item.contentId, true);
      try {
        await ContentService.downloadContent(item.contentId, item.filePath);
      } catch (error) {
        console.log("Error downloading content", error);
        this.$store.dispatch("showError", {
          title: "Error",
          message: "Failed to download resource",
        });
      } finally {
        this.$delete(this.loading.downloading, item.contentId);
      }
    },
    openReplaceDialog(resource) {
      this.dialog.selectedResource = resource;
      this.dialog.replace = true;
    },
    openDeleteDialog(resource) {
      this.dialog.selectedResource = resource;
      this.dialog.delete = true;
    },
    onContentReplaced() {
      this.$store.dispatch("showSuccess", { message: "Resource replaced successfully" });
      if (this.resources) {
        this.$emit("get-content");
      } else {
        this.getContent();
      }
    },
    onContentDeleted() {
      this.$store.dispatch("showSuccess", { message: "Resource deleted successfully" });
      this.$emit("remove-from-program", this.dialog.selectedResource);
      if (this.resources) {
        this.$emit("get-content");
      } else {
        this.getContent();
      }
    },
    onContentInUse(references) {
      this.dialog.inUseReferences = references;
      this.dialog.deleteError = true;
    },
    onContentError(error) {
      this.$store.dispatch("showError", {
        title: "Error",
        message: error?.error || "An error occurred",
      });
    },
    loadContentForm() {
      this.dialog.createContent = true;
    },
    close() {
      this.$emit("close");
    },
    addToProgram(resource) {
      // if (this.activeProgram) return;
      console.log("Adding resource to program ", resource);
      this.$emit("add-to-program", resource);
    },
    updateSortBy(option) {
      console.log("updating sortby ", option);

      var sortOption = this.sort.options.find((x) => x.values.includes(this.sort.value));
      // If they're picking an option that's already selected, then they're just changing the sort order.
      if (sortOption == option) {
        var asc = this.sort.value.includes("asc");
        this.sort.value = option.key + "-" + (asc ? "desc" : "asc");
      } else {
        this.sort.value = option.key + "-asc";
        // this.replaceQuery(option.key + "-asc", null);
      }
    },
    timeDiffMilliseconds(x) {
      // The time between now and the create date, in milliseconds
      const now = moment();
      const end = moment(x.createdAt);
      // console.log(now, end);
      // console.log('milliseconds', end.diff(now));
      return end.diff(now);
    },
  },
  computed: {
    ...mapState(["userProfile", "clients"]),
    currentProgramIsBlocking() {
      if (!this.programId || !this.dialog.inUseReferences.length) return false;
      return this.dialog.inUseReferences.some(
        (ref) => ref.type === "Program" && String(ref.id) === String(this.programId)
      );
    },
    inUseMessage() {
      if (!this.dialog.inUseReferences.length) return "";

      if (this.currentProgramIsBlocking && this.dialog.inUseReferences.length === 1) {
        return "This resource is attached to the program you're currently editing.\n\nSave your program changes first, then try deleting this resource.";
      }

      const learningModules = this.dialog.inUseReferences.filter(
        (ref) => ref.type === "Learning Module"
      );
      const programs = this.dialog.inUseReferences.filter((ref) => ref.type === "Program");
      let msg = "";
      if (learningModules.length) {
        const items = learningModules.map((ref) => `• ${ref.name}`).join("\n");
        msg += `The following learning modules are using this resource:\n${items}`;
      }
      if (programs.length) {
        if (msg) msg += "\n\n";
        const items = programs.map((ref) => `• ${ref.name}`).join("\n");
        msg += `The following programs are using this resource:\n${items}`;
      }
      if (this.currentProgramIsBlocking) {
        msg +=
          "\n\nThe program you're currently editing is in this list. Save your program changes first, then try deleting this resource.";
      } else {
        msg +=
          "\n\nRemove this resource from these items in order to delete the resource from the admin page.";
      }
      return msg;
    },
    visibleResources() {
      let resources = (this.resources || this.data.resources).slice();
      if (this.sort.value) {
        if (this.sort.value.includes("create-date")) {
          resources = resources
            .filter((x) => x.createdAt)
            .sort((a, b) => {
              if (isNaN(this.timeDiffMilliseconds(a))) {
                return 1;
              } else if (isNaN(this.timeDiffMilliseconds(b))) {
                return -1;
              }
              return this.sort.value.includes("asc")
                ? this.timeDiffMilliseconds(a) - this.timeDiffMilliseconds(b)
                : this.timeDiffMilliseconds(b) - this.timeDiffMilliseconds(a);
            });
          // .append(modulesToDisplay.filter((x) => !x.createdAt));
        } else {
          // console.log("sorting by name");
          // Sort by name
          resources = resources.sort((a, b) => {
            return this.sort.value.includes("asc")
              ? a.displayName.localeCompare(b.displayName)
              : b.displayName.localeCompare(a.displayName);
          });
        }
      }
      if (!this.data.search) {
        return resources;
      }
      resources = resources.filter((x) =>
        x.displayName.toLowerCase().includes(this.data.search.toLowerCase())
      );
      return resources;
    },
  },
};
</script>

<style scoped>
.resource-icons {
  border-radius: 4px;
  width: 40px;
  min-width: 40px;
  height: 40px;
}

.resource-box {
  flex: 1;
  min-width: 0;
  height: 40px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}
</style>
